/**
 * ASSIGN, FINISHED (P22, Roman's decision 7)
 *
 * =========================================================================
 * WHAT WAS WRONG WITH IT
 * =========================================================================
 * "Assign to" was a free-text e-mail box next to a button. Seven real clicks
 * were measured in the pilot and ALL SEVEN sent an empty string, which
 * `assign.ts` faithfully stored as `AssignedTo = NULL`. So the only thing the
 * feature ever did in production was silently un-assign tickets — and it
 * reported "Assignment updated" every time.
 *
 * Three changes, all of them about not lying:
 *   1. A PICKER of real DRA users, not a text field. A typo is no longer a
 *      valid owner, and the list says where it comes from.
 *   2. An empty selection is not a command. Removing an owner is its own
 *      button, and it says what it does before it does it.
 *   3. The current owner is stated, always — including "nobody", which is the
 *      state 35 of 46 live tickets are in.
 *
 * NO NOTIFICATION IS SENT. Roman's decision 7 and Bruno's E25: assignment is a
 * marker on a shared queue, not a task hand-off, and a mail per assignment in a
 * five-person pilot is noise nobody asked for.
 */

import { useState } from 'react';
import { UserCheck, UserMinus } from 'lucide-react';
import type { Assignee } from '../types/domain';

import { Button } from './ui/Button';
import { Card } from './ui/Card';
import { Select } from './ui/Field';
export default function AssignPanel({
  assignedTo, assignees, currentUserEmail, pending, onAssign,
}: {
  assignedTo: string | null;
  assignees: Assignee[];
  currentUserEmail: string | undefined;
  pending: boolean;
  onAssign: (email: string | null) => void;
}) {
  const [choice, setChoice] = useState('');

  // The signed-in user may hold no role row (an Admin reaching this through the
  // code override), so "assign to me" cannot rely on being in the list.
  const options = currentUserEmail && !assignees.some(a => a.email.toLowerCase() === currentUserEmail.toLowerCase())
    ? [{ email: currentUserEmail, roleNames: [] as string[] }, ...assignees]
    : assignees;

  return (
    /* P39: the kit's Card (so "Owner" is a real <h2>), the kit's Select (the bare
       one measured 21 px against §4's 32 px floor), and the kit's destructive
       variant for the removal — which was the last hand-rolled red-bordered
       button on this screen and differed from the kit only in ways nobody chose.
       Assign is SECONDARY now: §7 allows one primary per view and this screen's
       is the reply. */
    <Card
      title="Owner"
      action={
        <span className="text-xs text-muted">
          {assignedTo
            ? <>Currently <span className="font-medium text-body">{assignedTo}</span></>
            : <span className="italic">Nobody has taken this ticket.</span>}
        </span>
      }
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-60">
          <Select
            value={choice}
            onChange={e => setChoice(e.target.value)}
            aria-label="Assign to"
          >
            <option value="">Choose a colleague…</option>
            {options.map(a => (
              <option key={a.email} value={a.email}>
                {a.email}{a.roleNames.length > 0 ? ` — ${a.roleNames.join(', ')}` : ''}
              </option>
            ))}
          </Select>
        </div>
        <Button variant="secondary" size="sm"
          disabled={!choice || choice === assignedTo || pending}
          disabledReason="Pick a colleague from the list first."
          onClick={() => { onAssign(choice); setChoice(''); }}
        >
          <UserCheck className="h-3.5 w-3.5" /> Assign
        </Button>
        {currentUserEmail && currentUserEmail !== assignedTo && (
          <Button variant="secondary" size="sm"
            disabled={pending}
            onClick={() => onAssign(currentUserEmail)}
          >
            Take it
          </Button>
        )}
        {assignedTo && (
          <Button
            variant="destructive"
            size="sm"
            disabled={pending}
            onClick={() => onAssign(null)}
            title="Puts the ticket back in the unassigned queue. Nothing else changes and nobody is told."
          >
            <UserMinus className="h-3.5 w-3.5" /> Remove the assignment
          </Button>
        )}
      </div>

      <p className="mt-2 text-xs text-gray-500">
        The list holds everybody with an active DRA role — a colleague who has never been given one does not appear
        here until an Admin assigns them a role under Settings. Assigning sends no mail; it marks the queue.
      </p>
    </Card>
  );
}
